/**
 * The HR Director's approval — the one action in this app that settles the ledger.
 *
 * What happens on confirm, in a single transaction or not at all: every employee involved is
 * locked, the proposal's reservation is released, *then* the attendees' entitlement is read, and
 * the usage is written. That ordering is the design — reading before releasing sees the event's
 * own reservation and concludes its attendees have no room left, which reads as the money having
 * been spent twice.
 *
 * The dialog carries an **idempotency key** generated when it opens, so a double-tap or a retry
 * replays the first answer rather than settling again.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, idempotencyKey } from '@/lib/api';
import { ErrorNote, Money } from '@/components/bits';
import { Alert, Button, Dialog, Field, Textarea } from '@/components/ui';
import type { Claim, Settlement } from '@/lib/types';

export function ApprovalDialog({
  open,
  claim,
  settlement,
  onClose,
}: {
  open: boolean;
  claim: Claim;
  settlement: Settlement | undefined;
  onClose: () => void;
}) {
  const cache = useQueryClient();
  const [note, setNote] = useState('');
  const [key, setKey] = useState(() => idempotencyKey());

  useEffect(() => {
    if (open) setKey(idempotencyKey());
  }, [open]);

  const approve = useMutation({
    mutationFn: () =>
      api.approveClaim(
        claim.id,
        {
          action: 'approve',
          ...(note.trim() ? { note: note.trim() } : {}),
          expected_version: claim.version,
        },
        key,
      ),
    onSuccess: async () => {
      onClose();
      await cache.invalidateQueries({ queryKey: ['claim', claim.id] });
      await cache.invalidateQueries({ queryKey: ['settlement', claim.id] });
      await cache.invalidateQueries({ queryKey: ['claims'] });
      await cache.invalidateQueries({ queryKey: ['budget-departments'] });
      await cache.invalidateQueries({ queryKey: ['proposal', claim.proposal.id] });
      await cache.invalidateQueries({ queryKey: ['calculate-roster'] });
      await cache.invalidateQueries({ queryKey: ['calculate-selection'] });
    },
  });

  const blocked = claim.pending_exceptions > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Approve and settle"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            busy={approve.isPending}
            disabled={blocked}
            onClick={() => approve.mutate()}
          >
            Approve and settle
          </Button>
        </>
      }
    >
      <p className="text-muted-foreground">
        This writes the money. The reservation is released and the actual usage recorded against
        each attendee&rsquo;s entitlement, in one transaction.
      </p>

      {settlement ? (
        <dl className="grid grid-cols-2 gap-y-2">
          <dt className="text-muted-foreground">Total cost</dt>
          <dd className="text-right">
            <Money cents={settlement.total_cost_cents} />
          </dd>
          <dt className="text-muted-foreground">Reimbursable</dt>
          <dd className="text-right font-medium">
            <Money cents={settlement.reimbursable_cents} />
          </dd>
          <dt className="text-muted-foreground">Excess — not reimbursed</dt>
          <dd className="text-right">
            <Money cents={settlement.excess_cents} />
          </dd>
          <dt className="text-muted-foreground">Reservation released</dt>
          <dd className="text-right">
            <Money cents={settlement.outstanding_hold_cents} muted />
          </dd>
        </dl>
      ) : null}

      {blocked ? (
        <Alert tone="warn" title="An exception is still pending">
          Decide it first. Refused in the service and by a database trigger.
        </Alert>
      ) : null}

      {claim.chain.hr_verified_by !== null ? (
        <p className="text-muted-foreground text-xs">
          Verified by user #{claim.chain.hr_verified_by}. You must be someone else — a database
          CHECK refuses a claim whose verifier and approver are the same person (D-39).
        </p>
      ) : null}

      <Field label="Note (optional)">
        {(props) => (
          <Textarea {...props} value={note} onChange={(event) => setNote(event.target.value)} />
        )}
      </Field>

      {approve.error ? <ErrorNote error={approve.error} /> : null}
    </Dialog>
  );
}
